import { ArrowDown, ArrowLeftRight, ArrowUp, CreditCard, type LucideIcon } from 'lucide-react';
import { parseYmd, type TransactionType, type Ymd } from '@lira/shared';

/** Acento visual reutilizado por MoneyInput/Toggle (mapeia p/ tokens do DESIGN). */
export type TxAccent = 'expense' | 'income' | 'balance' | 'cards';

interface TypeMeta {
  /** Rótulo curto ("Despesa"). */
  label: string;
  /** Acento p/ MoneyInput/Toggle. */
  accent: TxAccent;
  /** Cor sólida (hex) p/ ícones/realces. */
  color: string;
  icon: LucideIcon;
  /** Classe do botão SALVAR. */
  btn: string;
  /** Classe do texto/realce. */
  text: string;
  /** Classe de fundo suave (cabeçalho do valor). */
  soft: string;
  /** Rótulo do toggle de efetivação. */
  effLabel: string;
  /** Rótulo do toggle "fixa" (só entradas). */
  fixedLabel: string;
}

export const TYPE_META: Record<TransactionType, TypeMeta> = {
  expense: {
    label: 'Despesa',
    accent: 'expense',
    color: '#F44336',
    icon: ArrowDown,
    btn: 'bg-expense hover:opacity-90',
    text: 'text-expense',
    soft: 'bg-expense-weak',
    effLabel: 'Foi paga',
    fixedLabel: 'Despesa fixa',
  },
  income: {
    label: 'Receita',
    accent: 'income',
    color: '#4CAF50',
    icon: ArrowUp,
    btn: 'bg-income hover:opacity-90',
    text: 'text-income',
    soft: 'bg-income-weak',
    effLabel: 'Foi recebida',
    fixedLabel: 'Receita fixa',
  },
  transfer: {
    label: 'Transferência',
    accent: 'balance',
    color: '#2979FF',
    icon: ArrowLeftRight,
    btn: 'bg-balance hover:opacity-90',
    text: 'text-balance',
    soft: 'bg-balance/10',
    effLabel: 'Foi efetivada',
    fixedLabel: '',
  },
};

/** Meta da despesa no cartão (M5): mesmo formulário da despesa, mas acento TEAL. */
export const CARD_EXPENSE_META: TypeMeta = {
  label: 'Despesa no cartão',
  accent: 'cards',
  color: '#00796B',
  icon: CreditCard,
  btn: 'bg-cards hover:opacity-90',
  text: 'text-cards',
  soft: 'bg-cards/10',
  effLabel: 'Foi paga',
  fixedLabel: 'Despesa fixa',
};

const MONTHS_SHORT = [
  'jan',
  'fev',
  'mar',
  'abr',
  'mai',
  'jun',
  'jul',
  'ago',
  'set',
  'out',
  'nov',
  'dez',
] as const;

/** '2026-05-13' -> '13 mai 2026' (mês abreviado, p/ exibição proeminente da data). */
export function formatDatePretty(ymd: Ymd): string {
  const { year, month, day } = parseYmd(ymd);
  return `${day} ${MONTHS_SHORT[month - 1]} ${year}`;
}

// --- Preferências locais ("última usada") --------------------------------

const LAST_ACCOUNT_KEY = 'lira:lastAccount';
const LAST_CARD_KEY = 'lira:lastCard';
const lastCategoryKey = (type: TransactionType): string => `lira:lastCategory:${type}`;

function readLS(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeLS(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* modo privado / storage cheio — segue sem persistir */
  }
}

export const getLastAccountId = (): string | null => readLS(LAST_ACCOUNT_KEY);
export const setLastAccountId = (id: string): void => writeLS(LAST_ACCOUNT_KEY, id);

export const getLastCardId = (): string | null => readLS(LAST_CARD_KEY);
export const setLastCardId = (id: string): void => writeLS(LAST_CARD_KEY, id);

export const getLastCategoryId = (type: TransactionType): string | null =>
  readLS(lastCategoryKey(type));
export const setLastCategoryId = (type: TransactionType, id: string): void =>
  writeLS(lastCategoryKey(type), id);
